import React, { useState, useMemo, useEffect } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Bell,
  CalendarClock,
  List,
  SlidersHorizontal,
  MapPin,
  Search,
  Printer, 
} from "lucide-react";
import api from "../../api/api"; 
import logo from "../../images/SPFLLOGO.png"; 


import ExcelExport from "../../UserControls/ExcelExport/ExcelExport";

// ✅ CRITICAL: Using named import
import { getCalibrationPrintHtml } from "./CalibrationPrintPage"; 

// =========================
// HELPER FUNCTION
// =========================
const getCalibrationStatus = (dueDateStr) => {
  const today = new Date(); 
  const dueDate = new Date(dueDateStr);

  if (isNaN(dueDate.getTime())) {
    return {
      text: "Invalid Date",
      key: "compliant",
      days: 9999,
      badgeClass: "badge-gray",
    };
  }

  const timeDiff = dueDate.getTime() - today.getTime();
  const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (dayDiff < 0) {
    return {
      text: `Overdue by ${Math.abs(dayDiff)} days`,
      key: "overdue",
      days: dayDiff,
      badgeClass: "badge-red",
    };
  }
  if (dayDiff === 0) {
    return { text: "Due Today", key: "dueSoon", days: 0, badgeClass: "badge-yellow" };
  }
  if (dayDiff <= 30) {
    return {
      text: `Due in ${dayDiff} days`,
      key: "dueSoon",
      days: dayDiff,
      badgeClass: "badge-yellow",
    };
  }
  return {
    text: `Due in ${dayDiff} days`,
    key: "compliant",
    days: dayDiff,
    badgeClass: "badge-green",
  };
};

// =========================
// COMPONENTS
// =========================

const StatCard = ({ title, count, icon, colorClass, onClick, isActive }) => {
  const Icon = icon;
  return (
    <button
      onClick={onClick}
      className={`stat-card ${colorClass.bg} ${colorClass.text} ${
        isActive ? colorClass.ring : ""
      }`}
      aria-pressed={isActive}
    >
      <div>
        <p className="stat-title">{title}</p>
        <p className="stat-count">{count}</p>
      </div>
      <div className="stat-icon">
        <Icon size={28} />
      </div>
    </button>
  );
};


const CalibrationTable = ({ items, onPrint }) => {
  if (items.length === 0) {
    return <p className="no-data">No items match this filter.</p>;
  }
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>AssetNo</th>
            <th>Asset Name</th>
            <th>Serial</th>
            <th>Type</th>
            <th>Next Due</th>
            <th>Location</th>
            <th>User</th>
            <th>Print</th> 
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.assetCalibrationId}>
              <td>
                <span className={`badge ${item.status.badgeClass}`}>
                  {item.status.text}
                </span>
              </td>
              
               <td>{item.AssetNo}</td>
              <td>{item.assetName}</td>
              <td>{item.serialNumber}</td>
              <td>{item.calibrationType}</td>
              <td>{item.nextDueDate}</td>
              <td>{item.currentLocation}</td>
              <td>{item.responsibleUser || "-"}</td>
              <td> 
                <button 
                  onClick={() => onPrint(item.assetid)} 
                  className="btn-print"
                  title={`Print details for ${item.AssetNo}`}
                >
                 🖨️ Print
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


const NavButton = ({ icon, label, isActive, onClick }) => {
  const Icon = icon;
  return (
    <button onClick={onClick} className={`nav-btn ${isActive ? "active" : ""}`}>
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );
};


 const companyName = sessionStorage.getItem("companyName");
  console.log("companyName from session:", companyName);
const AppNavigation = ({ activeApp, setActiveApp }) => (
  <nav className="nav">
    <div className="nav-inner">
<td colSpan="2" style={{ display: "flex", alignItems: "center" }}>
  {/* <img
    src={logo}
    alt="Company Logo"
    style={{
      width: "60px",
      height: "60px",
      borderRadius: "100%",
      backgroundColor: "white",
      boxShadow: "0 0 4px rgba(0,0,0,0.2)",
      padding: "5px",
      marginRight: "10px", 
    }}
  /> */}          
  <h1
    style={{
      fontSize: "22px",
      fontWeight: "bold",
      color: "#333",
      margin: 0,
      textAlign: "left",
    }}
  >
    {companyName}
  </h1>
  
</td>

      <div className="nav-buttons">
        <NavButton    
          icon={SlidersHorizontal}
          label="Calibration Dashboard"
          isActive={activeApp === "calibration"}
          onClick={() => setActiveApp("calibration")}
        />
      </div>
    </div>
  </nav>
);


// =========================
// MAIN DASHBOARD
// =========================

const CalibrationDashboard = ({ handlePrint }) => { 
  const [filter, setFilter] = useState("overdue");
  const [apiData, setApiData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Fetch API data
useEffect(() => {
  const fetchData = async () => {
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.CalibrationDashboardAPI(userName);

      console.log("API Response:", response); 

      const tableData = response?.Table || [];

      const formatted = tableData.map((row) => ({
        assetCalibrationId: row.AstCalId,
        assetid: row.assetid, 
        AssetNo: row.AssetNo,
        assetName: row.AssetName,
        serialNumber: row.serialNo,
        location: row.location,
        responsibleUser: row.responsibility || "-",
        calibrationType: row.ctypename,
        nextDueDate: row.dueDate,
        currentLocation: row.location,
        assetStatus: "Active",
      }));

      setApiData(formatted);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  fetchData();
}, []);



  const allItems = useMemo(
    () =>
      apiData
        .map((item) => ({
          ...item,
          status: getCalibrationStatus(item.nextDueDate),
        }))
        .sort((a, b) => a.status.days - b.status.days),
    [apiData]
  );

  const stats = useMemo(
    () =>
      allItems.reduce(
        (acc, item) => {
          if (item.status.key === "overdue") acc.overdue++;
          if (item.status.key === "dueSoon") acc.dueSoon++;
          if (item.status.key === "compliant") acc.compliant++;
          acc.all++;
          return acc;
        },
        { overdue: 0, dueSoon: 0, compliant: 0, all: 0 }
      ),
    [allItems]
  );
const filteredItems = useMemo(() => {
  let result = filter === "all"
    ? allItems
    : allItems.filter((item) => item.status.key === filter);

  if (searchTerm.trim() !== "") {
    const lower = searchTerm.toLowerCase();

    result = result.filter((item) =>
      (item.AssetNo?.toLowerCase() || "").includes(lower) ||
      (item.assetName?.toLowerCase() || "").includes(lower) ||
      (item.serialNumber?.toLowerCase() || "").includes(lower) ||
      (item.calibrationType?.toLowerCase() || "").includes(lower) ||
      (item.currentLocation?.toLowerCase() || "").includes(lower) ||
      (item.nextDueDate?.toLowerCase() || "").includes(lower)
    );
  }

  return result;
}, [allItems, filter, searchTerm]);

  const cardColors = {
    overdue: { bg: "bg-red", text: "text-white", ring: "ring-red" },
    dueSoon: { bg: "bg-yellow", text: "text-white", ring: "ring-yellow" },
    compliant: { bg: "bg-green", text: "text-white", ring: "ring-green" },
    all: { bg: "bg-white", text: "text-dark", ring: "ring-blue" },
  };

  const filterTitles = {
    overdue: "Overdue Items",
    dueSoon: "Items Due Soon",
    compliant: "Compliant Items",
    all: "All Items",
  };
 
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div>
          <h1>Calibration Dashboard</h1>
          <p>
            Today is{" "}
            <strong>{new Date().toLocaleDateString("en-US", { dateStyle: "long" })}</strong>
          </p>
        </div>
        <button onClick={() => setFilter("overdue")} className="btn-primary">
          <CalendarClock size={16} /> Quick: Show Overdue
        </button>
      </header>

      <div className="stat-grid">
        <StatCard
          title="Overdue"
          count={stats.overdue}
          icon={AlertTriangle}
          colorClass={cardColors.overdue}
          onClick={() => setFilter("overdue")}
          isActive={filter === "overdue"}
        />
        <StatCard
          title="Due in 30 Days"
          count={stats.dueSoon}
          icon={Bell}
          colorClass={cardColors.dueSoon}
          onClick={() => setFilter("dueSoon")}
          isActive={filter === "dueSoon"}
        />
        <StatCard
          title="Compliant"
          count={stats.compliant}
          icon={CheckCircle}
          colorClass={cardColors.compliant}
          onClick={() => setFilter("compliant")}
          isActive={filter === "compliant"}
        />
        <StatCard
          title="All Items"
          count={stats.all}
          icon={List}
          colorClass={cardColors.all}
          onClick={() => setFilter("all")}
          isActive={filter === "all"}
        />
      </div>

      <main className="table-section">
        <div className="table-header">
          <h2>{filterTitles[filter]}</h2>
          <div className="table-actions">

 <div style={{ 
  position: "relative", 
  display: "flex", 
  alignItems: "center" 
}}>
  <Search 
    size={16} 
    color="#6b7280" 
    style={{ 
      position: "absolute", 
      left: "8px" 
    }} 
  />

  <input
    type="text"
    placeholder="Search..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    style={{
      padding: "6px 10px 6px 28px", // ← left padding increased for icon
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "0.85rem",
       width: "300px",
    }}
  />
</div>

            <button onClick={() => setFilter("all")} className="btn-reset">
              Reset Filter
            </button>

<ExcelExport data={filteredItems} fileName="CalibrationDashboard" />

            <span>
              
              Showing <strong>{filteredItems.length}</strong> records
            </span>
          </div>
        </div>
        <CalibrationTable items={filteredItems} onPrint={handlePrint} />
      </main>
    </div>
  );
};


// =========================
// APP ROOT
// =========================
export default function App() {
  const [activeApp, setActiveApp] = useState("calibration");
  
  // Handler to fetch data, generate HTML, and open the new window/tab
  const handlePrintAsset = async (assetId) => {
    
    if (!assetId) {
        console.error("Asset ID is required for printing.");
        return;
    }

    // 1. CRITICAL FIX: Open the new window immediately upon button click
    // to prevent the browser's pop-up blocker from blocking it.
    const printWindow = window.open('', '_blank', 'width=1000,height=800,scrollbars=yes,resizable=yes');

    if (!printWindow) {
        alert("Could not open print window. Please check your browser's pop-up blocker settings.");
        return;
    }

    // Write a temporary loading message to the new window
    printWindow.document.write('<html><body><p style="text-align:center; margin-top: 50px;">Loading print data...</p></body></html>');
    printWindow.document.close();
    const logoSrc = new URL(logo, window.location.href).href;

    try {
        // 2. Fetch data from API
        const response = await api.AssetDeatilsHTMLprint(assetId);
        const userName = sessionStorage.getItem("userName");
        const companyResponse = await api.CompanyDetailsAPI(userName);

        const assetData = {
            main: response?.Table?.[0],
            calibrations: response?.Table1 || [],
            items: response?.Table2 || [],
            spares: response?.Table3 || [],
            Table4: companyResponse?.Table || [],
        };

        if (!assetData.main) {
            const errorHtml = '<html><body><p style="color:red; text-align:center; margin-top: 50px;">Failed to load asset details for printing.</p></body></html>';
            const blob = new Blob([errorHtml], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            printWindow.location.href = url;
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            return;
        }

        // 3. Generate HTML content using the utility function
        const htmlContent = getCalibrationPrintHtml(assetData,companyName,logoSrc);

        // 4. Use a blob URL to load the content into the already-open window
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        printWindow.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 10000);

    } catch (error) {
        console.error("Error during print process:", error);
        // Display error in the new window
        const errorHtml = '<html><body><p style="color:red; text-align:center; margin-top: 50px;">An error occurred while preparing the print document.</p></body></html>';
        const blob = new Blob([errorHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        printWindow.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };


  const AssetTracking = () => (
    <div className="asset-page">
      <div className="asset-box">
        <h1>Asset Movement Module</h1>
        <p>This is where the AssetTracking component content would be rendered.</p>
      </div>
    </div>
  );

  // Conditional rendering logic
  let content;

  if (activeApp === "calibration") {
    // Render the Dashboard, passing the new print handler
    content = <CalibrationDashboard handlePrint={handlePrintAsset} />; 
  } else if (activeApp === "movement") {
    content = <AssetTracking />;
  } else {
    content = null;
  }
  
  return (
    <>
      <style>{`
        // ... (Styles remain unchanged)
        body, html {
          margin: 0;
          font-family: "Inter", Arial, sans-serif;
          background-color: #f9fafb;
          color: #333;
        }
        .nav { background: #fff; border-bottom: 1px solid #e5e7eb; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
        .nav-inner { max-width: 1200px; margin: 0 auto; padding: 1rem 1.5rem; display: flex; align-items: center; gap: 2rem; }
        .nav-brand { font-weight: 600; color: #2563eb; font-size: 1.2rem; }
        .nav-buttons { display: flex; gap: 0.5rem; }
        .nav-btn { display: flex; align-items: center; gap: 6px; font-size: 0.9rem; padding: 6px 10px; border-radius: 5px; color: #555; background: transparent; border: none; cursor: pointer; }
        .nav-btn:hover { background: #f3f4f6; color: #111; }
        .nav-btn.active { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }

        .dashboard { max-width: 1200px; margin: 2rem auto; padding: 0 1.5rem; }
        .dashboard-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; }
        .dashboard-header h1 { font-size: 2rem; margin: 0; }
        .dashboard-header p { color: #6b7280; }
        .btn-primary {
          display: flex;
          align-items: center;
          gap: 4px; /* smaller space between icon and text */
          background: #2563eb;
          color: #fff;
          border: none;
          border-radius: 5px;
          padding: 5px 10px; /* smaller padding */
          font-size: 13px; /* reduced font size */
          cursor: pointer;
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

         .stat-grid { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); margin-bottom: 1.5rem; }

        .stat-card { padding: 0.rem 0.rem;  border-radius: 10px; 
        display: flex; justify-content: space-between;
         align-items: center; cursor: pointer; transition: all 0.2s ease; min-height: 80px; }
         
        .stat-card:hover { transform: translateY(-3px); box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
        .stat-title { font-size: 0.8rem; opacity: 0.8; }
        .stat-count { font-size: 1.8rem; font-weight: 600; margin-top: 0.3rem; }

        .bg-red { background: #dc2626; }
        .bg-yellow { background: #f59e0b; }
        .bg-green { background: #16a34a; }
        .bg-white { background: #fff; }
        .text-white { color: #fff; }
        .text-dark { color: #111; }
        .ring-red { outline: 3px solid rgba(239,68,68,0.3); }
        .ring-yellow { outline: 3px solid rgba(250,204,21,0.3); }
        .ring-green { outline: 3px solid rgba(34,197,94,0.3); }
        .ring-blue { outline: 3px solid rgba(59,130,246,0.3); }

        .table-section { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); padding: 1.5rem; }
        .table-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
        .table-actions { display: flex; align-items: center; gap: 10px; }
        .btn-reset { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 5px; padding: 5px 10px; cursor: pointer; }
        .btn-reset:hover { background: #f3f4f6; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th, .data-table td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; font-size: 0.9rem; }
        .data-table th { color: #6b7280; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.05em; }
        .data-table tr:hover { background: #f9fafb; }
        
        .btn-print { background: #e0e7ff; color: #2563eb; border: 1px solid #bfdbfe; border-radius: 5px; padding: 5px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
        .btn-print:hover { background: #c7d2fe; }

        .badge { display: inline-block; padding: 4px 10px; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; }
        .badge-red { background: #fee2e2; color: #b91c1c; }
        .badge-yellow { background: #fef3c7; color: #92400e; }
        .badge-green { background: #dcfce7; color: #166534; }
        .badge-gray { background: #f3f4f6; color: #374151; }

        .link-text { color: #2563eb; }
        .no-data { text-align: center; padding: 2rem; color: #9ca3af; }

        .asset-page { max-width: 1200px; margin: 2rem auto; padding: 1.5rem; }
        .asset-box { background: #fff; padding: 1.5rem; border-radius: 10px; box-shadow: 0 2px 6px rgba(0,0,0,0.05); }
        
        .print-container { max-width: 1000px; margin: 2rem auto; padding: 20px; background: #fff; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
        .print-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .print-details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
        .print-detail-item { font-size: 0.9rem; }
        .print-detail-item strong { display: block; font-size: 1rem; color: #333; margin-bottom: 2px; }
        .print-section-title { font-size: 1.25rem; border-bottom: 2px solid #333; padding-bottom: 5px; margin-top: 30px; margin-bottom: 15px; }
        
        @media print {
            .no-print { display: none !important; }
            .print-container { margin: 0; box-shadow: none; max-width: none; }
            body { background: #fff; }
        }

        .btn-print {
          background: #10b981;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          transition: 0.3s ease;
        }
        .btn-print:hover {
          background: #059669;
        }


      `}</style>
      
      <AppNavigation activeApp={activeApp} setActiveApp={setActiveApp} />
      <main>
        {content}
      </main>
    </>
  );
}
